/**
 * Compact relative-time label shared across surfaces that show "when did
 * this happen" next to a piece of content (notifications popup, comment
 * rail / popover, etc.). Coarsens with age — minutes within the last
 * hour, hours within the day, `Nd` for the past week, then an
 * abbreviated date — so a list mixing fresh and week-old items stays
 * visually balanced.
 *
 * Anything that doesn't parse falls back to the empty string; callers
 * tolerate a missing label without breaking layout.
 *
 * Lives in `lib/` (not co-located with notifications) because it's a
 * generic time-formatting utility with no notification-specific
 * behavior — moving it here keeps every consumer importing from a
 * single source of truth (per the no-re-export rule in `.cursorrules`)
 * and stops the comment surfaces from reaching into a
 * notification-named module for a generic helper.
 */
export function formatRelativeTime(iso, nowMs = Date.now()) {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then))
        return "";
    const diffMs = nowMs - then;
    if (diffMs < 0)
        return "just now"; // clock skew — don't render a negative
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60)
        return "just now";
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60)
        return `${diffMin}m`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24)
        return `${diffHr}h`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7)
        return `${diffDay}d`;
    return new Date(iso).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
    });
}
/**
 * Variant of `formatRelativeTime` that reads as a full phrase — appends
 * " ago" to the compact numeric durations (`2m` → `2m ago`, `3h` →
 * `3h ago`, `1d` → `1d ago`) but leaves the natural-language and date
 * fallbacks alone (`just now`, `May 12`). Use on surfaces that show a
 * single timestamp without other temporal context (e.g. collection
 * card meta lines), where the bare compact form is ambiguous.
 *
 * Returns the empty string for inputs that don't parse, matching the
 * base helper's no-render contract.
 */
export function formatRelativeTimeAgo(iso, nowMs = Date.now()) {
    const rel = formatRelativeTime(iso, nowMs);
    if (rel === "")
        return "";
    // Numeric durations (`2m`, `3h`, `1d`) all start with a digit; the
    // word-form label (`just now`) and date fallback (`May 12`) start
    // with a letter and stand on their own.
    if (/^\d/.test(rel))
        return `${rel} ago`;
    return rel;
}
